import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import {
  checkNip,
  useCreateLead,
  useUpdateLead,
  type Lead,
  type LeadDictionaries,
  type LeadPayload,
  type LeadSource,
  type NipCheck,
} from "@/features/leads/api/leads";

/**
 * Zakładanie i edycja karty firmy.
 *
 * NIP jest sprawdzany zanim cokolwiek zapiszemy — i to jedno pytanie odpowiada na
 * trzy rzeczy naraz: czy firma jest już w bazie, czy już z nami współpracuje i czy
 * nie prosiła o niekontaktowanie. Dane z rejestrów są PODPOWIEDZIĄ do kliknięcia,
 * nie automatem: biała lista bywa nieaktualna, a użytkownik i tak patrzy w formularz.
 */

const input = "input-base";
const labelClass = "section-label mb-1 block";

interface Props {
  open: boolean;
  onClose: () => void;
  dictionaries?: LeadDictionaries;
  lead?: Lead | null;
}

interface ContactDraft {
  first_name: string;
  last_name: string;
  position: string;
  email: string;
  phone: string;
}

const EMPTY_CONTACT: ContactDraft = {
  first_name: "",
  last_name: "",
  position: "",
  email: "",
  phone: "",
};

/** Starsze uwagi to czysty tekst — bez tej konwersji edytor zgubiłby nowe linie. */
function toEditorHtml(text: string): string {
  if (!text || text.trimStart().startsWith("<")) return text;
  const escape = (line: string) =>
    line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text
    .split("\n")
    .map((line) => `<p>${escape(line)}</p>`)
    .join("");
}

export function LeadFormModal({ open, onClose, dictionaries, lead }: Props) {
  const editing = !!lead;
  const create = useCreateLead();
  const update = useUpdateLead();

  const [form, setForm] = useState<LeadPayload>({ name: "" });
  const [contacts, setContacts] = useState<ContactDraft[]>([{ ...EMPTY_CONTACT }]);
  const [check, setCheck] = useState<NipCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setCheck(null);
    setContacts([{ ...EMPTY_CONTACT }]);
    setForm(
      lead
        ? {
            name: lead.name,
            nip: lead.nip,
            regon: lead.regon,
            krs: lead.krs,
            street: lead.street,
            postal_code: lead.postal_code,
            city: lead.city,
            website: lead.website,
            voivodeships: lead.voivodeships,
            source: lead.source,
            source_detail: lead.source_detail,
            notes: toEditorHtml(lead.notes),
          }
        : { name: "", source: "manual", voivodeships: [] },
    );
  }, [open, lead]);

  const runCheck = async (nip: string) => {
    const digits = nip.replace(/\D/g, "");
    if (digits.length !== 10) return;
    setChecking(true);
    try {
      setCheck(await checkNip(digits, lead?.id));
    } catch {
      setCheck(null);
    } finally {
      setChecking(false);
    }
  };

  const applyRegistry = () => {
    const registry = check?.registry;
    if (!registry?.found) return;
    const address = registry.registered_address ?? {};
    setForm((prev) => ({
      ...prev,
      // Nazwa z rejestru NIE nadpisuje tej, którą już mamy. Przy jednoosobowej
      // działalności biała lista podaje imię i nazwisko podatnika, więc
      // nadpisanie zamieniało „GEO-SERWIS Jan Kowalski" na „KOWALSKI JAN"
      // i nazwa handlowa przepadała bezpowrotnie.
      name: prev.name?.trim() || registry.name || "",
      regon: registry.regon ?? prev.regon,
      krs: registry.krs ?? prev.krs,
      street: [address.street, address.building_number].filter(Boolean).join(" ") || prev.street,
      postal_code: address.postal_code || prev.postal_code,
      city: address.city || prev.city,
    }));
  };

  const submit = () => {
    if (!form.name?.trim()) return setError("Nazwa firmy jest wymagana.");
    setError("");

    const payload: LeadPayload = {
      ...form,
      name: form.name.trim(),
      nip: (form.nip ?? "").replace(/\D/g, ""),
    };
    if (!editing) {
      payload.contacts = contacts.filter(
        (c) => c.first_name || c.last_name || c.email || c.phone,
      );
    }

    const onError = (err: unknown) => {
      const errors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
        .response?.data?.errors;
      const first = errors && Object.values(errors)[0];
      setError(Array.isArray(first) ? first[0] : "Nie udało się zapisać. Sprawdź dane.");
    };

    if (editing && lead) {
      update.mutate({ id: lead.id, ...payload }, { onSuccess: onClose, onError });
    } else {
      create.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  const blocking = check?.lead || check?.suppression;

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edycja leada" : "Nowy lead"} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={labelClass}>NIP</label>
            <div className="relative">
              <input
                className={input}
                value={form.nip ?? ""}
                onChange={(e) => setForm({ ...form, nip: e.target.value })}
                onBlur={(e) => runCheck(e.target.value)}
                placeholder="1234567890"
              />
              {checking && (
                <Loader2
                  size={14}
                  className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[var(--color-text-muted)]"
                />
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Nazwa firmy *</label>
            <input
              className={input}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
        </div>

        {check && <NipVerdict check={check} onApply={applyRegistry} />}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={labelClass}>Ulica i numer</label>
            <input
              className={input}
              value={form.street ?? ""}
              onChange={(e) => setForm({ ...form, street: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass}>Kod pocztowy</label>
            <input
              className={input}
              value={form.postal_code ?? ""}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass}>Miejscowość</label>
            <input
              className={input}
              value={form.city ?? ""}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Strona WWW</label>
            <input
              className={input}
              value={form.website ?? ""}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://"
            />
          </div>
          <div>
            <label className={labelClass}>Województwa działania</label>
            <MultiSelect
              options={dictionaries?.voivodeships ?? []}
              value={form.voivodeships ?? []}
              onChange={(values) => setForm({ ...form, voivodeships: values })}
              placeholder="Wszystkie / nieznane"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Źródło</label>
            <select
              className={input}
              value={form.source ?? "manual"}
              onChange={(e) => setForm({ ...form, source: e.target.value as LeadSource })}
            >
              {(dictionaries?.sources ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Szczegóły źródła</label>
            <input
              className={input}
              value={form.source_detail ?? ""}
              onChange={(e) => setForm({ ...form, source_detail: e.target.value })}
              placeholder="Kto polecił, jakie targi, jaki katalog…"
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Uwagi wewnętrzne</label>
          <RichTextEditor
            value={form.notes ?? ""}
            onChange={(html) => setForm((prev) => ({ ...prev, notes: html }))}
            placeholder="Uwagi wewnętrzne…"
          />
        </div>

        {!editing && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className={labelClass}>Osoby kontaktowe</span>
              <button
                type="button"
                onClick={() => setContacts([...contacts, { ...EMPTY_CONTACT }])}
                className="text-xs text-[var(--color-primary)]"
              >
                <Plus size={12} className="mr-1 inline" />
                Dodaj osobę
              </button>
            </div>
            <p className="mb-2 text-xs text-[var(--color-text-muted)]">
              Pierwsza osoba na liście jest kontaktem głównym — to do niej domyślnie
              pójdzie zapytanie.
            </p>
            <div className="space-y-2">
              {contacts.map((contact, index) => (
                <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-11">
                  <input
                    className={`${input} sm:col-span-2`}
                    placeholder="Imię"
                    value={contact.first_name}
                    onChange={(e) => updateContact(setContacts, index, { first_name: e.target.value })}
                  />
                  <input
                    className={`${input} sm:col-span-2`}
                    placeholder="Nazwisko"
                    value={contact.last_name}
                    onChange={(e) => updateContact(setContacts, index, { last_name: e.target.value })}
                  />
                  <input
                    className={`${input} sm:col-span-2`}
                    placeholder="Stanowisko"
                    value={contact.position}
                    onChange={(e) => updateContact(setContacts, index, { position: e.target.value })}
                  />
                  <input
                    className={`${input} sm:col-span-3`}
                    placeholder="E-mail"
                    value={contact.email}
                    onChange={(e) => updateContact(setContacts, index, { email: e.target.value })}
                  />
                  <input
                    className={`${input} sm:col-span-1`}
                    placeholder="Telefon"
                    value={contact.phone}
                    onChange={(e) => updateContact(setContacts, index, { phone: e.target.value })}
                  />
                  <button
                    type="button"
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] sm:col-span-1"
                    onClick={() => setContacts(contacts.filter((_, i) => i !== index))}
                    aria-label="Usuń osobę"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-sm text-[var(--color-primary)]">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            onClick={submit}
            loading={create.isPending || update.isPending}
            disabled={!editing && !!blocking}
          >
            {editing ? "Zapisz" : "Dodaj lead"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function updateContact(
  setContacts: React.Dispatch<React.SetStateAction<ContactDraft[]>>,
  index: number,
  patch: Partial<ContactDraft>,
) {
  setContacts((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
}

function NipVerdict({ check, onApply }: { check: NipCheck; onApply: () => void }) {
  return (
    <div className="space-y-2 border border-[var(--color-border)] p-3 text-sm">
      {check.lead && (
        <p className="flex items-start gap-2 text-[var(--color-primary)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Ten NIP jest już w bazie leadów:{" "}
            <Link to={`/leads/${check.lead.id}`} className="underline">
              {check.lead.name}
            </Link>{" "}
            ({check.lead.status_label}). Nie zakładaj drugiej karty.
          </span>
        </p>
      )}
      {check.contractor && (
        <p className="flex items-start gap-2 text-amber-600 dark:text-amber-500">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Ta firma już z nami współpracuje jako podwykonawca{" "}
            <Link to={`/contractors/${check.contractor.id}`} className="underline">
              {check.contractor.name}
            </Link>
            . Zimne zapytanie byłoby wpadką.
          </span>
        </p>
      )}
      {check.suppression && (
        <p className="flex items-start gap-2 text-[var(--color-primary)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            NIP jest na liście „nie kontaktować”
            {check.suppression.reason ? ` — ${check.suppression.reason}` : ""}.
          </span>
        </p>
      )}
      {check.registry_error && (
        <p className="text-[var(--color-text-muted)]">{check.registry_error}</p>
      )}
      {check.registry?.found && (
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-green-700 dark:text-green-500">
            <Check size={14} />
            Biała lista MF: {check.registry.name}
            {check.registry.vat_status ? ` (VAT: ${check.registry.vat_status})` : ""}
          </p>
          <Button size="sm" variant="secondary" onClick={onApply}>
            Uzupełnij z rejestru
          </Button>
        </div>
      )}
      {check.registry && !check.registry.found && !check.registry_error && (
        <p className="text-[var(--color-text-muted)]">
          NIP poprawny, ale nie ma go na białej liście MF — dane wpisz ręcznie.
        </p>
      )}
    </div>
  );
}
